import React, { useContext, useEffect } from "react";
import { CommonContext } from "./CommonContext";
import { getCurrentTime } from "utils/commonUtils";
import PropTypes from "prop-types";
import { common } from "utils/constant";

const SocketInit = ({ children }) => {
  const {
    socketRef,
    setPrivateChatMessages,
    setIsScreenSharedInMeeting,
    setScreenSharedStreams,
    isInMeetingRoom,
    setParticipantOfMeeting,
    setGeneralChatMessages,
    setGroupMessagesData,
    peers,
    room_id,
    userOwnData,
    userName,
    setIsInterviewEnded,
    setIsComeNewMessage,
  } = useContext(CommonContext);

  // ---------- chat & misc ----------
  useEffect(() => {
    if (!(isInMeetingRoom && socketRef.current)) return;

    const onGroupMsg = (data) => {
      const current_time = new Date().toLocaleTimeString("en-US", {
        hour: "numeric", minute: "numeric", hour12: true,
      });
      setGroupMessagesData((prev) => [
        ...prev,
        { username: data.username, message: data.message, isOwnMessage: false, time: current_time },
      ]);
      setIsComeNewMessage(common.group);
    };

    const onPrivateMsg = (data) => {
      const current_time = getCurrentTime();
      setPrivateChatMessages((prev) => [
        ...prev,
        {
          participantID: data.participantID,
          username: data.username,
          isOwnMessage: false,
          message: data.message,
          time: "" + current_time,
        },
      ]);
      setIsComeNewMessage(common.individual);
    };

    const onGeneralMsg = (data) => {
      const current_time = getCurrentTime();
      setGeneralChatMessages((prev) => [
        ...prev,
        { username: data.username, isOwnMessage: false, message: data.message, time: "" + current_time },
      ]);
      setIsComeNewMessage(common.general);
    };

    const onScreenStatus = (data) => {
      const { userId, isScreenSharing } = data;
      if (peers.current[userId]) {
        if (!peers.current[userId].__meta) peers.current[userId].__meta = {};
        peers.current[userId].__meta.isScreenSharing = isScreenSharing;
      }
    };

    const onStreamUpdated = () => {
      setIsScreenSharedInMeeting({ isShared: false, isSelfShared: false });
      setScreenSharedStreams({ userId: "", remoteStream: null, userNameValue: "", isScreenShare: false });
    };

    const onUserLeft = (data) => {
      const userId = data.userId;
      if (peers.current[userId]) {
        try { peers.current[userId].destroy(); } catch { }
        delete peers.current[userId];
      }
      setParticipantOfMeeting((prev) =>
        prev.map((u) => (u.guid === data.guid ? { ...u, isJoin: false } : u))
      );
    };

    const onForceLeave = () => {
      socketRef.current.emit("leave", {
        username: userName,
        userId: userOwnData.userId,
        room_id,
        guid: userOwnData.guid,
      });
      setIsInterviewEnded(true);
    };

    socketRef.current.on("group_message_received", onGroupMsg);
    socketRef.current.on("private_message_received", onPrivateMsg);
    socketRef.current.on("message_received", onGeneralMsg);
    socketRef.current.on("screen_sharing_status", onScreenStatus);
    socketRef.current.on("stream_updated", onStreamUpdated);
    socketRef.current.on("user_left", onUserLeft);
    socketRef.current.on("force_leave", onForceLeave);

    return () => {
      try {
        socketRef.current.off("group_message_received", onGroupMsg);
        socketRef.current.off("private_message_received", onPrivateMsg);
        socketRef.current.off("message_received", onGeneralMsg);
        socketRef.current.off("screen_sharing_status", onScreenStatus);
        socketRef.current.off("stream_updated", onStreamUpdated);
        socketRef.current.off("user_left", onUserLeft);
        // eslint-disable-next-line
        socketRef.current.off("force_leave", onForceLeave);
      } catch { }
    };
    // eslint-disable-next-line
  }, [isInMeetingRoom]);

  // ---------- device & AV state mirroring ----------
  // FIX: Use functional state updates (prev =>) so handlers never capture
  // participantOfMeeting in their closure. This removes it from the dependency
  // array, preventing the listener storm that caused mute state mismatches:
  //   signal received → state updates → effect re-runs → listeners re-registered
  //   → next signal lands on a freshly attached listener that may miss the event.
  useEffect(() => {
    if (!(isInMeetingRoom && socketRef.current)) return;

    const onReceiveCam = (data) => {
      console.log("[SocketInit] receive_camera_signal", data);
      setParticipantOfMeeting((prev) =>
        prev.map((u) =>
          u.guid === data.guid ? { ...u, isJoin: true, isCameraOn: data.isCameraOn } : u
        )
      );
    };

    const onReceiveAudio = (data) => {
      // Functional update — always reads latest state, no stale closure
      setParticipantOfMeeting((prev) =>
        prev.map((u) =>
          u.guid === data.guid ? { ...u, isJoin: true, isAudioOn: data.isAudioOn } : u
        )
      );
    };

    socketRef.current.on("receive_camera_signal", onReceiveCam);
    socketRef.current.on("receive_audio_signal", onReceiveAudio);

    return () => {
      try {
        socketRef.current.off("receive_camera_signal", onReceiveCam);
        // eslint-disable-next-line
        socketRef.current.off("receive_audio_signal", onReceiveAudio);
      } catch { }
    };
    // participantOfMeeting intentionally removed from deps —
    // functional updates make it unnecessary, eliminating the listener storm.
    // eslint-disable-next-line
  }, [isInMeetingRoom]);

  return <React.Fragment>{children}</React.Fragment>;
};

export default SocketInit;

SocketInit.propTypes = {
  children: PropTypes.node.isRequired,
};